import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useEffect } from 'react';

// Geographic features to display on the map
const geographicFeatures = {
  // Major Seas
  seas: [
    { name: 'AEGEAN SEA', position: [38.5, 25.0], size: 'large', rotation: -15 },
    { name: 'IONIAN SEA', position: [38.0, 18.5], size: 'large', rotation: 0 },
    { name: 'TYRRHENIAN SEA', position: [40.0, 12.0], size: 'large', rotation: 0 },
    { name: 'ADRIATIC SEA', position: [43.0, 15.5], size: 'medium', rotation: 45 },
    { name: 'LIBYAN SEA', position: [34.5, 24.0], size: 'medium', rotation: 0 },
  ],

  // Mountain Ranges
  mountains: [
    { name: 'PINDUS MOUNTAINS', position: [39.5, 21.2], size: 'medium', rotation: -25 },
    { name: 'APENNINES', position: [42.5, 13.5], size: 'medium', rotation: 60 },
    { name: 'ALPS', position: [45.5, 8.5], size: 'medium', rotation: 45 },
    { name: 'TAURUS MTS', position: [37.5, 32.0], size: 'small', rotation: 45 },
    { name: 'Mt. Olympus', position: [40.0859, 22.3584], size: 'small', rotation: 0, style: 'peak' },
    { name: 'Mt. Etna', position: [37.7510, 15.0], size: 'small', rotation: 0, style: 'peak' },
  ],

  // Major Cities & Regions
  cities: [
    { name: 'ATHENS', position: [37.9838, 23.7275], size: 'medium' },
    { name: 'SPARTA', position: [37.0739, 22.4297], size: 'small' },
    { name: 'ROME', position: [41.9028, 12.4964], size: 'medium' },
    { name: 'SYRACUSE', position: [37.0755, 15.2866], size: 'small' },
    { name: 'CARTHAGE', position: [36.8532, 10.3230], size: 'small' },
    { name: 'TROY', position: [39.9575, 26.2389], size: 'small' },
  ],

  // Regions
  regions: [
    { name: 'THRACE', position: [41.5, 26.0], size: 'large', rotation: 0, style: 'region' },
    { name: 'MACEDONIA', position: [40.8, 22.8], size: 'large', rotation: 0, style: 'region' },
    { name: 'PELOPONNESE', position: [37.5, 22.3], size: 'large', rotation: 0, style: 'region' },
    { name: 'SICILY', position: [37.5, 14.2], size: 'large', rotation: 0, style: 'region' },
    { name: 'EPIRUS', position: [39.5, 20.5], size: 'medium', rotation: 0, style: 'region' },
    { name: 'CRETE', position: [35.2, 25.0], size: 'medium', rotation: 0, style: 'region' },
    { name: 'CORSICA', position: [42.0, 9.0], size: 'small', rotation: 45, style: 'region' },
    { name: 'SARDINIA', position: [40.0, 9.0], size: 'medium', rotation: 0, style: 'region' },
    { name: 'CALABRIA', position: [38.9, 16.6], size: 'small', rotation: 0, style: 'region' },
    { name: 'ANATOLIA', position: [39.0, 33.0], size: 'xlarge', rotation: 0, style: 'region' },
  ],

  // Islands
  islands: [
    { name: 'Cyprus', position: [35.1264, 33.4299], size: 'small', style: 'island' },
    { name: 'Rhodes', position: [36.2048, 28.0], size: 'tiny', style: 'island' },
    { name: 'Lesbos', position: [39.2, 26.3], size: 'tiny', style: 'island' },
    { name: 'Cyclades', position: [37.0, 25.2], size: 'small', style: 'island' },
  ],
};

const GeographicLabels = () => {
  const map = useMap();

  useEffect(() => {
    const labels: L.Marker[] = [];

    // Function to create custom label icon
    const createLabelIcon = (
      text: string,
      size: string,
      rotation: number = 0,
      style?: string
    ) => {
      let fontSize = '16px';
      let fontWeight = 'normal';
      let letterSpacing = '2px';
      let opacity = '0.7';
      let color = '#5C4033'; // ink-secondary

      // Size variations
      if (size === 'xlarge') fontSize = '24px';
      else if (size === 'large') fontSize = '20px';
      else if (size === 'medium') fontSize = '16px';
      else if (size === 'small') fontSize = '13px';
      else if (size === 'tiny') fontSize = '11px';

      // Style variations
      if (style === 'region') {
        fontWeight = 'bold';
        letterSpacing = '4px';
        opacity = '0.5';
        color = '#2C1810'; // ink-primary
      } else if (style === 'peak') {
        fontWeight = '600';
        letterSpacing = '1px';
        opacity = '0.8';
      } else if (style === 'island') {
        fontWeight = '500';
        letterSpacing = '1.5px';
        opacity = '0.6';
      }

      return L.divIcon({
        html: `
          <div style="
            font-family: 'Forum', serif;
            font-size: ${fontSize};
            font-weight: ${fontWeight};
            letter-spacing: ${letterSpacing};
            color: ${color};
            opacity: ${opacity};
            text-shadow:
              1px 1px 2px rgba(245, 230, 200, 0.8),
              -1px -1px 2px rgba(245, 230, 200, 0.8);
            white-space: nowrap;
            transform: rotate(${rotation}deg);
            pointer-events: none;
            text-transform: uppercase;
          ">
            ${text}
          </div>
        `,
        className: 'geographic-label',
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      });
    };

    // Add sea labels
    geographicFeatures.seas.forEach((sea) => {
      const marker = L.marker([sea.position[0], sea.position[1]], {
        icon: createLabelIcon(sea.name, sea.size, sea.rotation),
        interactive: false,
      });
      marker.addTo(map);
      labels.push(marker);
    });

    // Add mountain labels
    geographicFeatures.mountains.forEach((mountain) => {
      const marker = L.marker([mountain.position[0], mountain.position[1]], {
        icon: createLabelIcon(mountain.name, mountain.size, mountain.rotation, mountain.style),
        interactive: false,
      });
      marker.addTo(map);
      labels.push(marker);
    });

    // Add city labels
    geographicFeatures.cities.forEach((city) => {
      const marker = L.marker([city.position[0], city.position[1]], {
        icon: createLabelIcon(city.name, city.size),
        interactive: false,
      });
      marker.addTo(map);
      labels.push(marker);
    });

    // Add region labels
    geographicFeatures.regions.forEach((region) => {
      const marker = L.marker([region.position[0], region.position[1]], {
        icon: createLabelIcon(region.name, region.size, region.rotation, region.style),
        interactive: false,
      });
      marker.addTo(map);
      labels.push(marker);
    });

    // Add island labels
    geographicFeatures.islands.forEach((island) => {
      const marker = L.marker([island.position[0], island.position[1]], {
        icon: createLabelIcon(island.name, island.size, 0, island.style),
        interactive: false,
      });
      marker.addTo(map);
      labels.push(marker);
    });

    // Cleanup on unmount
    return () => {
      labels.forEach((label) => map.removeLayer(label));
    };
  }, [map]);

  return null;
};

export default GeographicLabels;
